import { UnitType } from "encamp-prisma/generated/browser";

/**
 * Container type codes for tracked waste containers, aligned with the
 * DOT / RCRA manifest container types (EPA form 8700-22 item 10 and the
 * e-Manifest API), so a tracked container's type flows onto a manifest
 * request without back-mapping. The raw manifest tables store the same
 * codes as free-form strings (WasteLine.containerTypeCode).
 */
export const WASTE_CONTAINER_TYPE_CODES = [
  "BA",
  "CF",
  "CM",
  "CW",
  "CY",
  "DF",
  "DM",
  "DT",
  "DW",
  "HG",
  "TC",
  "TP",
  "TT",
] as const;

export type WasteContainerTypeCode = (typeof WASTE_CONTAINER_TYPE_CODES)[number];

export type WasteContainerTypeDefinition = Readonly<{
  code: WasteContainerTypeCode;
  label: string;
  /**
   * Prefill for the create/edit form where an industry-standard size
   * exists (55-gal drums, 275-gal IBC totes). Null where sizes vary too
   * widely to guess (boxes, cylinders, trucks) — the user enters it.
   */
  defaultCapacity: number | null;
  defaultCapacityUnit: UnitType | null;
}>;

export const WASTE_CONTAINER_TYPE_BY_CODE = {
  BA: {
    code: "BA",
    label: "Bag (burlap, cloth, paper, or plastic)",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  CF: {
    code: "CF",
    label: "Box, carton, or case (fiber or plastic)",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  CM: {
    code: "CM",
    label: "Box, carton, case, or roll-off (metal)",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  CW: {
    code: "CW",
    label: "Box, carton, or case (wooden)",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  CY: {
    code: "CY",
    label: "Cylinder",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  DF: {
    code: "DF",
    label: "Drum, barrel, or keg (fiberboard or plastic)",
    defaultCapacity: 55,
    defaultCapacityUnit: UnitType.GALLONS,
  },
  DM: {
    code: "DM",
    label: "Drum, barrel, or keg (metal)",
    defaultCapacity: 55,
    defaultCapacityUnit: UnitType.GALLONS,
  },
  DT: {
    code: "DT",
    label: "Dump truck",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  DW: {
    code: "DW",
    label: "Drum, barrel, or keg (wooden)",
    defaultCapacity: 55,
    defaultCapacityUnit: UnitType.GALLONS,
  },
  HG: {
    code: "HG",
    label: "Hopper or gondola car",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  TC: {
    code: "TC",
    label: "Tank car",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
  TP: {
    code: "TP",
    label: "Portable tank / tote (IBC)",
    defaultCapacity: 275,
    defaultCapacityUnit: UnitType.GALLONS,
  },
  TT: {
    code: "TT",
    label: "Cargo tank (tank truck)",
    defaultCapacity: null,
    defaultCapacityUnit: null,
  },
} satisfies Readonly<Record<WasteContainerTypeCode, WasteContainerTypeDefinition>>;

/** Pick-list order for forms; insertion order of the record above. */
export const WASTE_CONTAINER_TYPES: readonly WasteContainerTypeDefinition[] = Object.values(
  WASTE_CONTAINER_TYPE_BY_CODE
);

export function isWasteContainerTypeCode(value: string): value is WasteContainerTypeCode {
  return Object.prototype.hasOwnProperty.call(WASTE_CONTAINER_TYPE_BY_CODE, value);
}
